import { useEffect, useId, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { getScreenLanguage } from '../services/language-manager';
import { t } from '../services/language-manager';
import { SUPPORTED_LANGUAGES, getLanguageMeta, type LanguageCode } from '../i18n/types';

/** Header picker. Renders nothing while languages are switched off. */
export function LanguagePicker() {
  const { languagesEnabled, setLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const language = getScreenLanguage();
  const meta = getLanguageMeta(language);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!languagesEnabled) return null;

  const select = (code: LanguageCode) => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div className="language-picker" ref={rootRef}>
      <button
        type="button"
        className="language-picker__trigger"
        aria-label={t('language-aria')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="language-picker__flag" aria-hidden="true">{meta.flag}</span>
        <span className="language-picker__short">{meta.short}</span>
        <svg className="language-picker__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 4.5 L6 7.5 L9 4.5" />
        </svg>
      </button>
      {open && (
        <ul id={listId} className="language-picker__menu" role="listbox" aria-label={t('language-menu')}>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const selected = lang.code === language;
            return (
              <li key={lang.code} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={selected ? 'language-picker__option language-picker__option--selected' : 'language-picker__option'}
                  onClick={() => select(lang.code)}
                >
                  <span className="language-picker__flag" aria-hidden="true">{lang.flag}</span>
                  <span className="language-picker__native">{lang.nativeLabel}</span>
                  {selected && <span className="language-picker__check" aria-hidden="true">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
